/**
 * Settings › Privacy: who sees the action buttons on your posts (suggest, invite to a consortium, schedule a
 * meeting, send a message) — everyone, your connections (mutual follows and contacts) or nobody; each button
 * on its own, or all of them at once. Saved in the user's `post_actions` (/users/privacy-settings/).
 */
import React from 'react';
import { Box, CircularProgress, Divider, FormControl, MenuItem, Paper, Select, Stack, Typography } from '@mui/material';

const BUTTONS = [
  ['suggest', 'Suggest', 'Others suggest a call, consortium or person to you'],
  ['invite', 'Invite to consortium', 'Others invite you to one of their consortia'],
  ['meet', 'Schedule a meeting', 'Others propose a meeting with you'],
  ['message', 'Send a message', 'Others start a conversation with you'],
];
const AUDIENCES = [['everyone', 'Everyone'], ['connections', 'Connections only'], ['nobody', 'Nobody']];

function AudienceSelect({ value, onChange, disabled, mixed }) {
  return (
    <FormControl size="small" sx={{ minWidth: 180 }}>
      <Select value={mixed ? 'mixed' : value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
        {mixed && <MenuItem value="mixed" disabled>Mixed</MenuItem>}
        {AUDIENCES.map(([v, label]) => <MenuItem key={v} value={v}>{label}</MenuItem>)}
      </Select>
    </FormControl>
  );
}

export default function PostActionsSettings({ value = {}, onChange, saving }) {
  const current = (key) => value[key] || 'everyone';
  const all = BUTTONS.map(([key]) => current(key));
  const same = all.every((v) => v === all[0]);
  const set = (key, audience) => onChange({ ...value, [key]: audience });
  const setAll = (audience) => onChange(Object.fromEntries(BUTTONS.map(([key]) => [key, audience])));

  return (
    <Paper elevation={0} sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
        <Typography variant="h6" fontWeight={600}>Action buttons on your posts</Typography>
        {saving && <CircularProgress size={16} />}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose who sees each button beside your name on your posts. Connections are mutual follows and contacts.
      </Typography>
      <Stack spacing={1.5} divider={<Divider flexItem />}>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Box flex={1} minWidth={200}>
            <Typography variant="body1" fontWeight={600}>All buttons</Typography>
            <Typography variant="body2" color="text.secondary">The same choice for every button</Typography>
          </Box>
          <AudienceSelect value={all[0]} mixed={!same} onChange={setAll} disabled={saving} />
        </Box>
        {BUTTONS.map(([key, label, description]) => (
          <Box key={key} display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Box flex={1} minWidth={200}>
              <Typography variant="body1" fontWeight={500}>{label}</Typography>
              <Typography variant="body2" color="text.secondary">{description}</Typography>
            </Box>
            <AudienceSelect value={current(key)} onChange={(v) => set(key, v)} disabled={saving} />
          </Box>
        ))}
      </Stack>
    </Paper>
  );
}
